import React, { useState, useRef, useEffect } from 'react';
import { Download, Copy, Check, RefreshCw, Settings, Image, FileType, Search, Link2, Loader2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

type LogoType = 'full' | 'icon-only' | 'text-only' | 'stacked';
type LogoFormat = 'svg' | 'png' | 'jpg' | 'webp';
type ColorScheme = 'blue' | 'red' | 'green' | 'purple' | 'orange' | 'custom';

interface LogoConfig {
  text: string;
  icon: string;
  width: number;
  height: number;
  type: LogoType;
  format: LogoFormat;
  bgColor: string;
  iconColor: string;
  textColor: string;
  colorScheme: ColorScheme;
  padding: number;
  iconScale: number;
  fontSize: number;
  quality: number;
  fontFamily: string;
  fontWeight: string;
}

const popularIcons = [
  'Box', 'Zap', 'Rocket', 'Star', 'Heart', 'Flame', 'Crown', 'Diamond',
  'Circle', 'Square', 'Triangle', 'Hexagon', 'Layers', 'Package',
  'ShoppingBag', 'Coffee', 'Cloud', 'Database', 'Server', 'Cpu',
  'Code', 'Terminal', 'Smartphone', 'Laptop', 'Globe', 'Mail',
  'User', 'Users', 'Building', 'Home', 'Store', 'Truck',
];

const LogoGeneratorPage: React.FC = () => {
  const [config, setConfig] = useState<LogoConfig>({
    text: 'MyBrand',
    icon: 'Box',
    width: 400,
    height: 150,
    type: 'full',
    format: 'png',
    bgColor: 'transparent',
    iconColor: '#2563eb',
    textColor: '#1f2937',
    colorScheme: 'blue',
    padding: 15,
    iconScale: 100,
    fontSize: 100,
    quality: 100,
    fontFamily: 'sans-serif',
    fontWeight: 'bold',
  });

  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [publicUrl, setPublicUrl] = useState<string>('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const presetSizes = [
    { name: 'Email Signature', width: 360, height: 128 },
    { name: 'Twitter Header', width: 1500, height: 500 },
    { name: 'Facebook Cover', width: 820, height: 312 },
    { name: 'LinkedIn Banner', width: 1584, height: 396 },
    { name: 'Instagram Post', width: 1080, height: 1080 },
    { name: 'Square Icon', width: 512, height: 512 },
    { name: 'Wide Banner', width: 728, height: 90 },
    { name: 'Business Card', width: 600, height: 200 },
  ];

  const colorSchemes = [
    { id: 'blue', name: 'Blue', icon: '#2563eb', text: '#1f2937' },
    { id: 'red', name: 'Red', icon: '#dc2626', text: '#1f2937' },
    { id: 'green', name: 'Green', icon: '#16a34a', text: '#1f2937' },
    { id: 'purple', name: 'Purple', icon: '#9333ea', text: '#1f2937' },
    { id: 'orange', name: 'Orange', icon: '#ea580c', text: '#1f2937' },
    { id: 'custom', name: 'Custom', icon: '#2563eb', text: '#1f2937' },
  ];

  const fontOptions = [
    { id: 'sans-serif', name: 'Sans Serif', font: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    { id: 'serif', name: 'Serif', font: 'Georgia, "Times New Roman", serif' },
    { id: 'mono', name: 'Monospace', font: '"Courier New", Courier, monospace' },
    { id: 'rounded', name: 'Rounded', font: '"Comic Sans MS", "Marker Felt", sans-serif' },
  ];

  useEffect(() => {
    generateLogo();
  }, [config]);

  const getIconComponent = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent || Icons.Box;
  };

  const renderIconToCanvas = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) => {
    const IconComponent = getIconComponent(config.icon);
    const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="24" height="24" fill="none"/></svg>`;

    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const scale = size / 24;
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    if (config.icon === 'Box') {
      ctx.beginPath();
      ctx.moveTo(12, 2);
      ctx.lineTo(22, 8.5);
      ctx.lineTo(22, 15.5);
      ctx.lineTo(12, 22);
      ctx.lineTo(2, 15.5);
      ctx.lineTo(2, 8.5);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(12, 2);
      ctx.lineTo(12, 22);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(12, 2);
      ctx.lineTo(2, 8.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(12, 2);
      ctx.lineTo(22, 8.5);
      ctx.stroke();
    } else if (config.icon === 'Zap') {
      ctx.beginPath();
      ctx.moveTo(13, 2);
      ctx.lineTo(3, 14);
      ctx.lineTo(12, 14);
      ctx.lineTo(11, 22);
      ctx.lineTo(21, 10);
      ctx.lineTo(12, 10);
      ctx.closePath();
      ctx.fill();
    } else if (config.icon === 'Star') {
      ctx.beginPath();
      ctx.moveTo(12, 2);
      ctx.lineTo(15, 9);
      ctx.lineTo(22, 9.27);
      ctx.lineTo(16.18, 14.97);
      ctx.lineTo(18.18, 21.02);
      ctx.lineTo(12, 17);
      ctx.lineTo(5.82, 21.02);
      ctx.lineTo(7.82, 14.97);
      ctx.lineTo(2, 9.27);
      ctx.lineTo(9, 9);
      ctx.closePath();
      ctx.fill();
    } else if (config.icon === 'Heart') {
      ctx.beginPath();
      ctx.moveTo(12, 21.35);
      ctx.bezierCurveTo(10.55, 20.03, 5.4, 15.36, 2, 11.28);
      ctx.bezierCurveTo(-0.5, 8.48, -0.24, 4.5, 3.22, 3.03);
      ctx.bezierCurveTo(5.5, 2.03, 8.5, 2.5, 12, 5.09);
      ctx.bezierCurveTo(15.5, 2.5, 18.5, 2.03, 20.78, 3.03);
      ctx.bezierCurveTo(24.24, 4.5, 24.5, 8.48, 22, 11.28);
      ctx.bezierCurveTo(18.6, 15.36, 13.45, 20.03, 12, 21.35);
      ctx.closePath();
      ctx.fill();
    } else if (config.icon === 'Circle') {
      ctx.beginPath();
      ctx.arc(12, 12, 10, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(12, 12, 8, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  };

  const generateLogo = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = 2;
    canvas.width = config.width * scale;
    canvas.height = config.height * scale;
    ctx.scale(scale, scale);

    ctx.clearRect(0, 0, config.width, config.height);

    if (config.bgColor !== 'transparent') {
      ctx.fillStyle = config.bgColor;
      ctx.fillRect(0, 0, config.width, config.height);
    }

    const padding = config.width * (config.padding / 100);
    const availableWidth = config.width - padding * 2;
    const availableHeight = config.height - padding * 2;

    if (config.type === 'icon-only') {
      const iconSize = Math.min(availableWidth, availableHeight) * 0.7 * (config.iconScale / 100);
      const iconX = (config.width - iconSize) / 2;
      const iconY = (config.height - iconSize) / 2;
      renderIconToCanvas(ctx, iconX, iconY, iconSize, config.iconColor);
    } else if (config.type === 'text-only') {
      const baseFontSize = Math.min(availableHeight * 0.6, availableWidth * 0.15);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = fontOptions.find(f => f.id === config.fontFamily)?.font || fontOptions[0].font;
      ctx.font = `${config.fontWeight} ${fontSize}px ${font}`;
      ctx.fillStyle = config.textColor;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(config.text, config.width / 2, config.height / 2);
    } else if (config.type === 'stacked') {
      const iconSize = Math.min(availableWidth, availableHeight * 0.5) * 0.7 * (config.iconScale / 100);
      const iconX = (config.width - iconSize) / 2;
      const iconY = padding;
      renderIconToCanvas(ctx, iconX, iconY, iconSize, config.iconColor);

      const baseFontSize = Math.min(availableHeight * 0.3, availableWidth * 0.12);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = fontOptions.find(f => f.id === config.fontFamily)?.font || fontOptions[0].font;
      ctx.font = `${config.fontWeight} ${fontSize}px ${font}`;
      ctx.fillStyle = config.textColor;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(config.text, config.width / 2, padding + iconSize + fontSize);
    } else {
      const iconSize = availableHeight * 0.7 * (config.iconScale / 100);
      const iconX = padding;
      const iconY = (config.height - iconSize) / 2;
      renderIconToCanvas(ctx, iconX, iconY, iconSize, config.iconColor);

      const textX = padding + iconSize + padding * 0.5;
      const textWidth = availableWidth - iconSize - padding * 0.5;
      const baseFontSize = Math.min(availableHeight * 0.5, textWidth * 0.2);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = fontOptions.find(f => f.id === config.fontFamily)?.font || fontOptions[0].font;
      ctx.font = `${config.fontWeight} ${fontSize}px ${font}`;
      ctx.fillStyle = config.textColor;
      ctx.textBaseline = 'middle';
      ctx.fillText(config.text, textX, config.height / 2);
    }

    if (config.format === 'svg') {
      setPreviewUrl('');
    } else {
      const format = config.format === 'jpg' ? 'jpeg' : config.format;
      const quality = config.quality / 100;
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
          }
        },
        `image/${format}`,
        quality
      );
    }
  };

  const generateSVG = (): string => {
    const padding = config.width * (config.padding / 100);
    const availableWidth = config.width - padding * 2;
    const availableHeight = config.height - padding * 2;

    const bg = config.bgColor !== 'transparent'
      ? `<rect width="${config.width}" height="${config.height}" fill="${config.bgColor}"/>`
      : '';

    const IconComponent = getIconComponent(config.icon);

    let content = '';

    if (config.type === 'icon-only') {
      const iconSize = Math.min(availableWidth, availableHeight) * 0.7 * (config.iconScale / 100);
      const iconX = (config.width - iconSize) / 2;
      const iconY = (config.height - iconSize) / 2;
      content = `<g transform="translate(${iconX}, ${iconY}) scale(${iconSize / 24})">
        <rect width="24" height="24" fill="none" stroke="${config.iconColor}" stroke-width="2"/>
      </g>`;
    } else if (config.type === 'text-only') {
      const baseFontSize = Math.min(availableHeight * 0.6, availableWidth * 0.15);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = config.fontFamily;
      content = `<text x="${config.width / 2}" y="${config.height / 2}" font-family="${font}" font-size="${fontSize}" font-weight="${config.fontWeight}" fill="${config.textColor}" text-anchor="middle" dominant-baseline="middle">${config.text}</text>`;
    } else if (config.type === 'stacked') {
      const iconSize = Math.min(availableWidth, availableHeight * 0.5) * 0.7 * (config.iconScale / 100);
      const iconX = (config.width - iconSize) / 2;
      const iconY = padding;
      const baseFontSize = Math.min(availableHeight * 0.3, availableWidth * 0.12);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = config.fontFamily;
      content = `
        <g transform="translate(${iconX}, ${iconY}) scale(${iconSize / 24})">
          <rect width="24" height="24" fill="none" stroke="${config.iconColor}" stroke-width="2"/>
        </g>
        <text x="${config.width / 2}" y="${padding + iconSize + fontSize}" font-family="${font}" font-size="${fontSize}" font-weight="${config.fontWeight}" fill="${config.textColor}" text-anchor="middle" dominant-baseline="middle">${config.text}</text>
      `;
    } else {
      const iconSize = availableHeight * 0.7 * (config.iconScale / 100);
      const iconX = padding;
      const iconY = (config.height - iconSize) / 2;
      const textX = padding + iconSize + padding * 0.5;
      const textWidth = availableWidth - iconSize - padding * 0.5;
      const baseFontSize = Math.min(availableHeight * 0.5, textWidth * 0.2);
      const fontSize = baseFontSize * (config.fontSize / 100);
      const font = config.fontFamily;
      content = `
        <g transform="translate(${iconX}, ${iconY}) scale(${iconSize / 24})">
          <rect width="24" height="24" fill="none" stroke="${config.iconColor}" stroke-width="2"/>
        </g>
        <text x="${textX}" y="${config.height / 2}" font-family="${font}" font-size="${fontSize}" font-weight="${config.fontWeight}" fill="${config.textColor}" dominant-baseline="middle">${config.text}</text>
      `;
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${config.width}" height="${config.height}" xmlns="http://www.w3.org/2000/svg">
  ${bg}
  ${content}
</svg>`;
  };

  const downloadLogo = () => {
    if (config.format === 'svg') {
      const svgContent = generateSVG();
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.text.toLowerCase().replace(/\s+/g, '-')}-logo-${config.width}x${config.height}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${config.text.toLowerCase().replace(/\s+/g, '-')}-logo-${config.width}x${config.height}.${config.format}`;
            a.click();
            URL.revokeObjectURL(url);
          }
        },
        `image/${config.format === 'jpg' ? 'jpeg' : config.format}`,
        config.quality / 100
      );
    }
  };

  const copyToClipboard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob }),
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const updateConfig = (updates: Partial<LogoConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const applyPreset = (preset: { width: number; height: number }) => {
    updateConfig(preset);
  };

  const applyColorScheme = (scheme: typeof colorSchemes[0]) => {
    updateConfig({
      colorScheme: scheme.id as ColorScheme,
      iconColor: scheme.icon,
      textColor: scheme.text,
    });
  };

  const filteredIcons = popularIcons.filter(icon =>
    icon.toLowerCase().includes(iconSearch.toLowerCase())
  );

  const generatePublicUrl = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsUploading(true);
    setPublicUrl('');

    try {
      const blob = await new Promise<Blob | null>((resolve) => {
        if (config.format === 'svg') {
          const svgContent = generateSVG();
          resolve(new Blob([svgContent], { type: 'image/svg+xml' }));
        } else {
          canvas.toBlob(
            (blob) => resolve(blob),
            `image/${config.format === 'jpg' ? 'jpeg' : config.format}`,
            config.quality / 100
          );
        }
      });

      if (!blob) {
        throw new Error('Failed to generate logo blob');
      }

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const fileName = `${config.text.toLowerCase().replace(/\s+/g, '-')}-${timestamp}-${randomId}.${config.format}`;
      const filePath = `public/${fileName}`;

      const { data, error } = await supabase.storage
        .from('logos')
        .upload(filePath, blob, {
          contentType: blob.type,
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      setPublicUrl(urlData.publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to generate public URL. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const copyPublicUrl = async () => {
    if (!publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50">
      <Navbar />

      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Custom Logo Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create professional logos with custom text, icons, colors, and formats
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Image className="h-6 w-6 mr-2 text-blue-600" />
              Preview
            </h2>

            <div className="relative bg-gray-100 rounded-lg p-8 mb-6 flex items-center justify-center min-h-[300px] border-2 border-gray-200">
              <div className="absolute top-2 right-2 text-xs text-gray-500 bg-white px-2 py-1 rounded">
                {config.width} × {config.height}px
              </div>
              <canvas
                ref={canvasRef}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  width: 'auto',
                  height: 'auto',
                }}
                className="shadow-lg"
              />
            </div>

            <div className="space-y-3">
              <div className="flex gap-3">
                <button
                  onClick={downloadLogo}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download {config.format.toUpperCase()}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center justify-center px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
                  title="Copy image to clipboard"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>

              <button
                onClick={generatePublicUrl}
                disabled={isUploading}
                className="w-full flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5 mr-2" />
                    Generate Public URL
                  </>
                )}
              </button>

              {publicUrl && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border-2 border-green-200 rounded-lg p-4"
                >
                  <p className="text-sm font-semibold text-green-900 mb-2">Public URL Generated!</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={publicUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-green-300 rounded text-sm"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <button
                      onClick={copyPublicUrl}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                      title="Copy URL"
                    >
                      {urlCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    Share this URL anywhere! The logo is publicly accessible.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 max-h-[800px] overflow-y-auto"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-blue-600" />
              Configuration
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Logo Text</label>
                <input
                  type="text"
                  value={config.text}
                  onChange={(e) => updateConfig({ text: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  placeholder="Enter your brand name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Icon</label>
                <button
                  onClick={() => setShowIconPicker(!showIconPicker)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg hover:border-blue-500 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center">
                    {React.createElement(getIconComponent(config.icon), { className: "h-6 w-6 mr-2", style: { color: config.iconColor } })}
                    <span>{config.icon}</span>
                  </div>
                  <Search className="h-5 w-5 text-gray-400" />
                </button>

                {showIconPicker && (
                  <div className="mt-2 p-4 border-2 border-gray-200 rounded-lg bg-gray-50 max-h-64 overflow-y-auto">
                    <input
                      type="text"
                      value={iconSearch}
                      onChange={(e) => setIconSearch(e.target.value)}
                      placeholder="Search icons..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3"
                    />
                    <div className="grid grid-cols-6 gap-2">
                      {filteredIcons.map((iconName) => {
                        const IconComp = getIconComponent(iconName);
                        return (
                          <button
                            key={iconName}
                            onClick={() => {
                              updateConfig({ icon: iconName });
                              setShowIconPicker(false);
                              setIconSearch('');
                            }}
                            className={`p-3 rounded-lg hover:bg-blue-100 transition-colors ${
                              config.icon === iconName ? 'bg-blue-200' : 'bg-white'
                            }`}
                            title={iconName}
                          >
                            <IconComp className="h-6 w-6" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Quick Presets</label>
                <div className="grid grid-cols-2 gap-2">
                  {presetSizes.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className="px-3 py-2 bg-gray-100 hover:bg-blue-100 hover:text-blue-700 rounded-lg text-sm font-medium transition-colors text-left"
                    >
                      <div className="font-semibold">{preset.name}</div>
                      <div className="text-xs text-gray-500">
                        {preset.width} × {preset.height}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Width (px)</label>
                  <input
                    type="number"
                    value={config.width}
                    onChange={(e) => updateConfig({ width: parseInt(e.target.value) || 400 })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="50"
                    max="3000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Height (px)</label>
                  <input
                    type="number"
                    value={config.height}
                    onChange={(e) => updateConfig({ height: parseInt(e.target.value) || 150 })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="50"
                    max="3000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Logo Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'full', name: 'Icon + Text' },
                    { id: 'icon-only', name: 'Icon Only' },
                    { id: 'text-only', name: 'Text Only' },
                    { id: 'stacked', name: 'Stacked' },
                  ].map((type) => (
                    <button
                      key={type.id}
                      onClick={() => updateConfig({ type: type.id as LogoType })}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        config.type === type.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <FileType className="h-4 w-4 mr-2" />
                  Export Format
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {(['svg', 'png', 'jpg', 'webp'] as LogoFormat[]).map((format) => (
                    <button
                      key={format}
                      onClick={() => updateConfig({ format })}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        config.format === format
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Color Scheme</label>
                <div className="grid grid-cols-3 gap-2">
                  {colorSchemes.map((scheme) => (
                    <button
                      key={scheme.id}
                      onClick={() => applyColorScheme(scheme)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        config.colorScheme === scheme.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {scheme.name}
                    </button>
                  ))}
                </div>
              </div>

              {config.colorScheme === 'custom' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Background</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.bgColor === 'transparent' ? '#ffffff' : config.bgColor}
                        onChange={(e) => updateConfig({ bgColor: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.bgColor}
                        onChange={(e) => updateConfig({ bgColor: e.target.value })}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm"
                        placeholder="transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Icon Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.iconColor}
                        onChange={(e) => updateConfig({ iconColor: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.iconColor}
                        onChange={(e) => updateConfig({ iconColor: e.target.value })}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Text Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={config.textColor}
                        onChange={(e) => updateConfig({ textColor: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.textColor}
                        onChange={(e) => updateConfig({ textColor: e.target.value })}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
              >
                <Settings className="h-4 w-4 mr-2" />
                {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
              </button>

              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 pt-4 border-t-2 border-gray-200"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Font Family</label>
                    <select
                      value={config.fontFamily}
                      onChange={(e) => updateConfig({ fontFamily: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {fontOptions.map((font) => (
                        <option key={font.id} value={font.id}>
                          {font.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Font Weight</label>
                    <select
                      value={config.fontWeight}
                      onChange={(e) => updateConfig({ fontWeight: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="600">Semi-Bold</option>
                      <option value="800">Extra Bold</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Padding: {config.padding}%
                    </label>
                    <input
                      type="range"
                      value={config.padding}
                      onChange={(e) => updateConfig({ padding: parseInt(e.target.value) })}
                      className="w-full"
                      min="0"
                      max="30"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Icon Scale: {config.iconScale}%
                    </label>
                    <input
                      type="range"
                      value={config.iconScale}
                      onChange={(e) => updateConfig({ iconScale: parseInt(e.target.value) })}
                      className="w-full"
                      min="50"
                      max="150"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Font Size: {config.fontSize}%
                    </label>
                    <input
                      type="range"
                      value={config.fontSize}
                      onChange={(e) => updateConfig({ fontSize: parseInt(e.target.value) })}
                      className="w-full"
                      min="50"
                      max="150"
                    />
                  </div>

                  {config.format !== 'svg' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Quality: {config.quality}%
                      </label>
                      <input
                        type="range"
                        value={config.quality}
                        onChange={(e) => updateConfig({ quality: parseInt(e.target.value) })}
                        className="w-full"
                        min="10"
                        max="100"
                      />
                    </div>
                  )}

                  <button
                    onClick={() =>
                      updateConfig({
                        padding: 15,
                        iconScale: 100,
                        fontSize: 100,
                        quality: 100,
                        fontFamily: 'sans-serif',
                        fontWeight: 'bold',
                      })
                    }
                    className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reset Advanced Settings
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-8 mt-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Format Guide</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-2">SVG</h3>
              <p className="text-sm text-gray-700">
                Perfect quality at any size. Best for web and digital use. Smallest file size.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-bold text-green-900 mb-2">PNG</h3>
              <p className="text-sm text-gray-700">
                Supports transparency. Best for logos with transparent backgrounds.
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <h3 className="font-bold text-yellow-900 mb-2">JPG</h3>
              <p className="text-sm text-gray-700">
                Smaller file size. Best for photos and solid backgrounds only.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="font-bold text-purple-900 mb-2">WebP</h3>
              <p className="text-sm text-gray-700">
                Modern format. Smaller than PNG with transparency support.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default LogoGeneratorPage;
